import type { DeliveryTicketExtraction, TicketBox } from './api';
import { normalizeTicketDate, resolveDeliveredTons, type TicketApplyDraft } from './deliveryTicket';

// A normalized box scaled to the rendered image, padded so a tight AI box is still
// a visible highlight. Returns CSS px; clamped to the image bounds.
export function scaleBox(
  box: TicketBox,
  imgW: number,
  imgH: number,
  pad = 1.5
): { left: number; top: number; width: number; height: number } {
  const x = Number(box.x) || 0;
  const y = Number(box.y) || 0;
  const w = Number(box.w) || 0;
  const h = Number(box.h) || 0;

  const padW = (w * (pad - 1)) / 2;
  const padH = (h * (pad - 1)) / 2;

  const nx = Math.max(0, x - padW);
  const ny = Math.max(0, y - padH);
  const nw = Math.min(1 - nx, w * pad);
  const nh = Math.min(1 - ny, h * pad);

  return { left: nx * imgW, top: ny * imgH, width: nw * imgW, height: nh * imgH };
}

export type ApplyField = 'quantity' | 'ticketNumber' | 'poNumber' | 'deliveryDate';

// The value a box represents, resolved from the extraction (delivered tons and
// dates are normalized the same way the apply draft is).
export function boxValue(box: TicketBox, extraction: DeliveryTicketExtraction): string {
  if (box.field === 'netTons') {
    const t = resolveDeliveredTons(extraction);
    return t != null ? String(t) : (extraction.netTons ?? '').trim();
  }
  if (box.field === 'deliveryDate') {
    return normalizeTicketDate(extraction.deliveryDate);
  }
  return String((extraction as any)[box.field] ?? '').trim();
}

// Assign a box's value to a chosen load field (fixes AI mis-mapping).
export function assignBoxToField(
  draft: TicketApplyDraft,
  box: TicketBox,
  extraction: DeliveryTicketExtraction,
  target: ApplyField
): TicketApplyDraft {
  return { ...draft, [target]: boxValue(box, extraction) };
}
